import { useCallback, useEffect, useRef, useState } from 'react'
import { connectedSourcesApi } from '@/lib/api'
import type { ConnectedSourceConnector } from '@/lib/api'

export function useLibrarySources() {
  const [connectors, setConnectors] = useState<Array<ConnectedSourceConnector>>([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [busy, setBusy] = useState('')
  const [actionError, setActionError] = useState(false)
  const pollRef = useRef<number | null>(null)
  const listRef = useRef<ReturnType<typeof connectedSourcesApi.list> | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await (listRef.current ??= connectedSourcesApi.list())
      setConnectors(result.connectors)
      setUnavailable(false)
    } catch {
      setUnavailable(true)
    } finally {
      listRef.current = null
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    const refresh = () => { void load() }
    window.addEventListener('focus', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      if (pollRef.current !== null) window.clearInterval(pollRef.current)
    }
  }, [load])

  const connect = async (slug: string) => {
    setBusy(slug)
    setActionError(false)
    // Open synchronously while the click still carries the user's activation.
    const popup = window.open('about:blank', '_blank')
    if (!popup) {
      setActionError(true)
      setBusy('')
      return
    }
    popup.opener = null
    try {
      const { url } = await connectedSourcesApi.link(slug)
      popup.location.href = url
      if (pollRef.current !== null) window.clearInterval(pollRef.current)
      const started = Date.now()
      pollRef.current = window.setInterval(() => {
        void load()
        if (Date.now() - started > 60_000 && pollRef.current !== null) {
          window.clearInterval(pollRef.current)
          pollRef.current = null
        }
      }, 2000)
    } catch {
      popup.close()
      setActionError(true)
    } finally {
      setBusy('')
    }
  }

  const disconnect = async (slug: string) => {
    setBusy(slug)
    setActionError(false)
    try {
      await connectedSourcesApi.disconnect(slug)
      await load()
    } catch {
      setActionError(true)
    } finally {
      setBusy('')
    }
  }
  return { connectors, loading, unavailable, busy, actionError, load, connect, disconnect }
}
